import { EstadoEntidade } from "@/shared/enumeradores";
import axios from "./axios-handler.service";
import { Produto } from "./produtos.service";
const BASE_URL = "/api/Revistas";

export interface Revista {
  estadoEntidade: EstadoEntidade;
  idRevista: string;
  idCatalogo: string;
  nome: string;
  descricao: string;
  dataCadastro: Date;
  dataEfetivacao: Date;
  ativo: boolean;
  paginas: PaginaRevista;
  quantidadeProdutos: number;
  quantidadePaginas: number;
  capa: string;
  capaCompleta: string;
}

export interface PaginaRevista {
  idPaginaRevista: string;
  idRevista: string;
  numeroPagina: number;
  imagem: string;
  imagemCompleta: string;
  produtos: ProdutoPaginaRevista[];
}

export interface ProdutoPaginaRevista {
  estadoEntidade: EstadoEntidade;
  idProdutoPaginaRevista: string;
  idPaginaRevista: string;
  idProduto: string;
  valor: number;
  valorPromocional: number;
  descricao: string;
  imagem: string;
  imagemCompleta: string;
}

export interface ArquivoImagem {
  nomeArquivo: string;
  arquivoBase64: string;
}

export class RevistasService {
  static async listarRevistasDoCatalogo(
    idCatalogo: string
  ): Promise<Revista[]> {
    const res = await axios.get<Revista[]>(
      `${BASE_URL}/ListarRevistasDoCatalogo`,
      {
        params: { idCatalogo },
      }
    );

    return res.data;
  }

  static async listarPaginasDaRevista(
    idRevista: string
  ): Promise<PaginaRevista[]> {
    const res = await axios.get<PaginaRevista[]>(
      `${BASE_URL}/ListarPaginasDaRevista`,
      {
        params: { idRevista },
      }
    );

    return res.data;
  }

  static async listarPaginasDaRevistaComProdutos(
    idRevista: string
  ): Promise<PaginaRevista[]> {
    const res = await axios.get<PaginaRevista[]>(
      `${BASE_URL}/ListarPaginasDaRevistaComProdutos`,
      {
        params: { idRevista },
      }
    );

    return res.data;
  }

  static async listarProdutosPorCatalogoParaVenda(
    idCatalogo: string,
    descricao: string,
    codigoFornecedor: string
  ): Promise<Produto[]> {
    const res = await axios.get<Produto[]>(
      `${BASE_URL}/ListarProdutosPorCatalogoParaVenda`,
      {
        params: { idCatalogo, descricao, codigoFornecedor },
      }
    );

    return res.data;
  }

  static async listarProdutosDaPagina(
    idPaginaRevista: string
  ): Promise<ProdutoPaginaRevista[]> {
    const res = await axios.get<ProdutoPaginaRevista[]>(
      `${BASE_URL}/ListarProdutosDaPagina`,
      {
        params: { idPaginaRevista },
      }
    );

    return res.data;
  }
  static async obterRevistaAtivaDoCatalogo(
    idCatalogo: string
  ): Promise<Revista> {
    const response = await axios.get<Revista>(
      `${BASE_URL}/ObterRevistaAtivaDoCatalogo`,
      {
        params: { idCatalogo },
      }
    );
    return response.data;
  }
  static async obterPaginaDaRevistaComProdutos(
    idRevista: string,
    numeroPagina: number
  ): Promise<PaginaRevista> {
    const res = await axios.get<PaginaRevista>(
      `${BASE_URL}/ObterPaginaDaRevistaComProdutos`,
      {
        params: { idRevista, numeroPagina },
      }
    );

    return res.data;
  }
  static async obterPaginaRevista(
    idRevista: string,
    idPaginaRevista: string
  ): Promise<PaginaRevista> {
    const res = await axios.get<PaginaRevista>(
      `${BASE_URL}/ObterPaginaRevista`,
      {
        params: { idRevista, idPaginaRevista },
      }
    );

    return res.data;
  }
  static async obterProdutoPaginaRevista(
    idProdutoPaginaRevista: string,
    idPaginaRevista: string
  ): Promise<ProdutoPaginaRevista> {
    const res = await axios.get<ProdutoPaginaRevista>(
      `${BASE_URL}/ObterProdutoPaginaRevista`,
      {
        params: { idProdutoPaginaRevista, idPaginaRevista },
      }
    );

    return res.data;
  }
  static async obterRevista(idRevista: string): Promise<Revista> {
    const res = await axios.get<Revista>(`${BASE_URL}/ObterRevista`, {
      params: { idRevista },
    });

    return res.data;
  }

  static async manterRevista(
    revista: Revista,
    arquivos: ArquivoImagem[]
  ): Promise<void> {
    const res = await axios.post(
      `${BASE_URL}/ManterRevista`,
      { revista, arquivos },
      { maxContentLength: 100000000, maxBodyLength: 1000000000 }
    );

    return res.data;
  }

  static async manterProdutoPaginaRevista(
    produtoPaginaRevista: ProdutoPaginaRevista
  ): Promise<ProdutoPaginaRevista> {
    const res = await axios.post(
      BASE_URL + "/ManterProdutoPaginaRevista",
      produtoPaginaRevista
    );
    return res.data;
  }

  static async adicionarPaginaRevista(
    paginaRevista: PaginaRevista,
    arquivoBase64: string
  ): Promise<void> {
    await axios.post(BASE_URL + "/AdicionarPaginaRevista", {
      paginaRevista,
      arquivoBase64,
    });
  }

  static async alterarImagemPaginaRevista(
    paginaRevista: PaginaRevista,
    arquivoBase64: string
  ): Promise<void> {
    await axios.post(BASE_URL + "/AlterarImagemPaginaRevista", {
      paginaRevista,
      arquivoBase64,
    });
  }

  static async deletarPaginaRevistaEProdutos(
    paginaRevista: PaginaRevista
  ): Promise<void> {
    await axios.post(BASE_URL + "/DeletarPaginaRevistaEProdutos", {
      numeroPagina: paginaRevista.numeroPagina,
      idRevista: paginaRevista.idRevista,
      idPaginaRevista: paginaRevista.idPaginaRevista,
    });
  }

  static async deletarProdutoDaPaginaRevista(
    produtoPaginaRevista: ProdutoPaginaRevista
  ): Promise<void> {
    await axios.post(BASE_URL + "/DeletarProdutoDaPaginaRevista", {
      idPaginaRevista: produtoPaginaRevista.idPaginaRevista,
      idProdutoPaginaRevista: produtoPaginaRevista.idProdutoPaginaRevista,
    });
  }

  static async trocarNumeroPaginaRevista(
    paginaRevista1: PaginaRevista,
    paginaRevista2: PaginaRevista
  ): Promise<void> {
    await axios.post(BASE_URL + "/TrocarNumeroPaginaRevista", {
      paginaRevista1,
      paginaRevista2,
    });
  }

  static async ativarRevistaDoCatalogo(
    idRevista: string,
    idCatalogo: string
  ): Promise<void> {
    await axios.post(BASE_URL + "/AtivarRevistaDoCatalogo", {
      idRevista,
      idCatalogo,
    });
  }

  static async deletarRevistaCompleta(idRevista: string): Promise<void> {
    await axios.post(BASE_URL + "/DeletarRevistaCompleta", {
      params: {
        idRevista,
      },
    });
  }
}
